import { useWhoDrugFilterOptions } from "./useWhoDrugFilterOptions";
import { usePublishWhoDrugReview, useWhoDrugWorkspace } from "@/features/page_agent/shared/context/AgentCapabilityContext";
import type { WhoDrugRecommendation } from "@/features/page_agent/shared/model/whoDrugReview";
import { useWhoDrugAgentAction } from "./useWhoDrugAgentAction";
import { useEffect, useRef, useState } from "react";
import type { C3Release, ProductsContext, WhoDrugRecodePreview } from "@/lib/api/generated/casedb";
import type { CaseReviewCollectionApi } from "../../shared/hooks/useCaseReview";
import { previewWhoDrugRecode } from "../../shared/services/catalogSectionService";
import { toCatalogApiError } from "../../shared/utils/catalogErrors";
import { whoDrugReleases, searchWhoDrug } from "../services/whodrugApi";
import { dictionaryRow, entryDetails, previewRows, WHODRUG_STATUS_LABELS, DEVICE_PRODUCT_TYPE_ID } from "../model/whodrugPresentation";

const DRUG_INFORMATION_GROUP = "Drug Information";

export function useWhoDrugRecode(api: CaseReviewCollectionApi, sectionContext: unknown, activeGroup?: string) {
  const action = api.whodrugRecode;
  const product = api.rows.find(row => row.recordId === api.selectedId);
  const context = (sectionContext as ProductsContext | undefined)?.products?.find(row => row.record_id === api.selectedId);
  const available = (!activeGroup || activeGroup === DRUG_INFORMATION_GROUP) && !!product && Number(product.values.product_type) !== DEVICE_PRODUCT_TYPE_ID && context?.whodrug_recode_available !== false;
  const { setHost } = useWhoDrugWorkspace();
  const [agentCollapsed, setAgentCollapsed] = useState(false);
  const [suggestionRationale, setSuggestionRationale] = useState("");
  const suggestionSelection = useRef<number | null>(null);
  const [agentShown, setAgentShown] = useState(false);
  const [tab, setTab] = useState<"search" | "compare" | "agent">("search");
  const [recommendations, setRecommendations] = useState<Array<WhoDrugRecommendation & { name: string; code: string; version: string }>>([]);
  const [open, setOpen] = useState(false);
  const [releases, setReleases] = useState<C3Release[]>([]);
  const [releaseId, setReleaseId] = useState(0);
  const filters = useWhoDrugFilterOptions(open, releaseId);
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [mah, setMah] = useState("");
  const [offset, setOffset] = useState(0);
  const [results, setResults] = useState<ReturnType<typeof dictionaryRow>[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingReleases, setLoadingReleases] = useState(false);
  const [selectedRid, setSelectedRid] = useState("");
  const [preview, setPreview] = useState<WhoDrugRecodePreview>();
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState("");
  const [searchError, setSearchError] = useState("");
  const previewRef = useRef<HTMLElement | null>(null);
  const lookup = useRef(0);
  const session = useRef(0);
  useEffect(() => {
    setRecommendations([]); setAgentShown(false); setTab("search");
    setOpen(false); setPreview(undefined); setSelectedRid(""); setSuggestionRationale(""); suggestionSelection.current = null; setError("");
    setApplying(false); setPreviewing(false); session.current++; lookup.current++;
  }, [api.caseId, api.selectedId, available]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingReleases(true); setSearchError("");
    whoDrugReleases().then(items => {
      if (cancelled) return;
      setReleases(items); setReleaseId(current => items.some(item => item.id === current) ? current : items[0]?.id ?? 0);
      if (!items.length) setSearchError("No ready WHODrug release is available. Import a dictionary before recoding.");
    }).catch(failure => { if (!cancelled) setSearchError(toCatalogApiError(failure).message); })
      .finally(() => { if (!cancelled) setLoadingReleases(false); });
    return () => { cancelled = true; };
  }, [open]);
  useEffect(() => {
    if (!open || !releaseId || query.trim().length < 2) {
      setResults([]); setHasMore(false); setSearching(false); return;
    }
    const controller = new AbortController();
    setSearching(true); setResults([]); setHasMore(false); setSearchError("");
    const timer = setTimeout(() => {
      searchWhoDrug(releaseId, query.trim(), offset, controller.signal, country.trim(), mah.trim()).then(page => {
        if (controller.signal.aborted) return;
        setResults((page.entries ?? []).map(dictionaryRow)); setHasMore(page.hasMore ?? false);
      }).catch(failure => { if (!controller.signal.aborted) setSearchError(toCatalogApiError(failure).message); })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [open, releaseId, query, offset, country, mah]);
  const clearSelection = () => { setSuggestionRationale(""); lookup.current++; setSelectedRid(""); setPreview(undefined); setPreviewing(false); setError(""); };
  const start = () => {
    if (!available || !action || action.disabled) return;
    clearSelection(); setResults([]); setOffset(0); setRecommendations([]); setAgentShown(false); setTab("search");
    setCountry(""); setMah("");
    setQuery(String(product?.values.product_name || product?.values.trade_name || ""));
    setOpen(true); session.current++;
  };
  const showPreview = (result: WhoDrugRecodePreview, rid: string, rationale = "") => {
    setSelectedRid(rid); setPreview(result); setPreviewing(false); setError(""); setSuggestionRationale(rationale);
    setTab("compare"); setAgentCollapsed(true);
    requestAnimationFrame(() => previewRef.current?.focus());
  };
  const choose = async (rid: string, rationale = "") => {
    if (!api.caseId || applying || action?.disabled) return;
    setTab("compare");
    requestAnimationFrame(() => previewRef.current?.focus());
    const id = ++lookup.current;
    setSelectedRid(rid); setSuggestionRationale(""); setPreview(undefined); setPreviewing(true); setError("");
    try {
      const result = await previewWhoDrugRecode(api.caseId, Number(api.selectedId), releaseId, rid);
      if (id === lookup.current) showPreview(result, rid, rationale);
    } catch (failure) { if (id === lookup.current) setError(toCatalogApiError(failure).message); }
    finally { if (id === lookup.current) setPreviewing(false); }
  };
  const apply = async () => {
    if (!preview?.fingerprint || !selectedRid || !action || action.disabled || applying) return;
    const id = ++session.current;
    setApplying(true); setError("");
    try {
      const saved = await action.run(api.selectedId, releaseId, selectedRid, preview.fingerprint);
      if (id === session.current && saved) { setOpen(false); clearSelection(); }
    } catch (failure) {
      if (id !== session.current) return;
      const issue = toCatalogApiError(failure);
      if (issue.deadlineChange) { setOpen(false); clearSelection(); }
      else setError(issue.findings.length ? issue.findings.map(finding => finding.message).join("\n") : issue.message);
    } finally { if (id === session.current) setApplying(false); }
  };
  const close = () => { if (!applying) { setOpen(false); clearSelection(); session.current++; } };
  const offer = async (recommendation: WhoDrugRecommendation) => {
    if (!api.caseId) return false;
    const current = session.current;
    const result = await previewWhoDrugRecode(api.caseId, Number(api.selectedId), releaseId, recommendation.rid);
    if (current !== session.current) return false;
    if (result.entry?.candidate?.record?.rid !== recommendation.rid || result.entry?.release?.id !== releaseId) {
      throw new Error("The dictionary returned a different entry or release. Request a new recommendation.");
    }
    const name = result.entry.candidate.record.drugName ?? "Unnamed entry";
    const code = result.entry.candidate.record.drugCode ?? "";
    const version = result.entry.release.shortVersion ?? "";
    if (suggestionSelection.current !== null && suggestionSelection.current === lookup.current) {
      lookup.current++;
      showPreview(result, recommendation.rid, recommendation.rationale);
      return true;
    }
    setRecommendations(items => [...items.filter(item => item.rid !== recommendation.rid), {
      ...recommendation, name, code, version,
    }]);
    return true;
  };
  usePublishWhoDrugReview(open && api.caseId && available && action && !action.disabled && !applying && releaseId
    ? { caseId: api.caseId, productId: api.selectedId, releaseId, offer } : null);
  const showAgent = () => { suggestionSelection.current = lookup.current; setAgentCollapsed(false); setAgentShown(true); setTab("agent"); };
  const genie = useWhoDrugAgentAction(api.selectedId, releaseId,
    open && available && !!action && !action.disabled && !applying && !loadingReleases, showAgent);
  const changes = previewRows(preview);
  return {
    genie, available, open, start, close, previewRef, setHost, agentShown, agentCollapsed, suggestionRationale, tab,
    setTab: (value: "search" | "compare" | "agent") => { setTab(value); if (value === "agent") setAgentCollapsed(false); },
    toggleAgent: () => { setAgentCollapsed(!agentCollapsed); setTab(agentCollapsed ? "agent" : "compare"); }, recommendations,
    discardSelection: () => { if (!applying) clearSelection(); },
    reviewRecommendation: (rid: string) => {
      const entry = recommendations.find(item => item.rid === rid);
      if (entry) { setRecommendations(items => items.filter(item => item.rid !== rid)); void choose(rid, entry.rationale); }
    },
    disabled: !action || action.disabled || applying, disabledReason: action?.reason,
    productName: String(product?.values.product_name || "Unnamed product"), currentCode: String(product?.values.drug_code || "Not coded"),
    status: context?.whodrug_status ? WHODRUG_STATUS_LABELS[context.whodrug_status] ?? context.whodrug_status : "Not searched in WHODrug",
    savedRid: context?.whodrug_rid, savedVersion: context?.whodrug_version,
    filters,
    country, changeCountry: (value: string) => { clearSelection(); setCountry(value); setOffset(0); },
    mah, changeMah: (value: string) => { clearSelection(); setMah(value); setOffset(0); },
    clearFilters: () => { clearSelection(); setCountry(""); setMah(""); setOffset(0); },
    query, changeQuery: (value: string) => { clearSelection(); setQuery(value); setOffset(0); },
    releases, releaseId, changeRelease: (id: number) => { session.current++; setRecommendations([]); clearSelection(); setReleaseId(id); setCountry(""); setMah(""); setOffset(0); },
    offset, previous: () => { clearSelection(); setOffset(Math.max(0, offset - 20)); },
    next: () => { clearSelection(); setOffset(offset + 20); }, hasMore,
    results, selectedRid, choose, searching, loadingReleases, previewing, applying, searchError, error,
    details: entryDetails(preview), changes, warnings: preview?.warnings ?? [],
    changeCount: changes.filter(row => row.changed).length,
    canApply: !!preview?.fingerprint && !previewing && !applying && !!action && !action.disabled,
    apply, resultSummary: results.length ? `${offset + 1}–${offset + results.length}` : "0",
  };
}
export type WhoDrugRecodeBinding = ReturnType<typeof useWhoDrugRecode>;
